/**
 * Stage-Finance timing controls — visible values must match engine use.
 * Sale months are offsets from permit; land acquisition is an absolute month.
 * No silent equal-phase merges: pre < post < late within [0, delivery−permit].
 */

import { clampInt, n } from './math.js';

/** Programme bounds from already-clamped duration fields (no hydrate recursion). */
export function programmeBoundsFromDurations(a = {}) {
  const prepMonths = Math.max(0, Math.round(n(a.prepMonths)));
  const authMonths = Math.max(0, Math.round(n(a.authMonths)));
  const constructionMonths = Math.max(1, Math.round(n(a.constructionMonths)));
  const deliveryBufferMonths = Math.max(0, Math.round(n(a.deliveryBufferMonths)));
  const constructionStartOffsetFromAuth = Math.max(
    0,
    Math.round(n(a.constructionStartOffsetFromAuth ?? 2)),
  );
  const prepEnd = prepMonths;
  const authorizationEnd = prepEnd + authMonths;
  const permitMonth = Math.max(1, authorizationEnd);
  const constructionStart = permitMonth + constructionStartOffsetFromAuth;
  const constructionEnd = constructionStart + constructionMonths;
  const deliveryMonth = constructionEnd + deliveryBufferMonths;
  const totalMonths = Math.max(1, deliveryMonth);
  const defaultAcquisition = Math.max(1, prepEnd || 1);
  const maxSaleOffset = Math.max(0, deliveryMonth - permitMonth);
  /** Three distinct sale months need offsets 0 < 1 < 2 → at least 2 months after permit. */
  const threeSaleWindowFeasible = maxSaleOffset >= 2;

  return {
    prepEnd,
    authorizationEnd,
    permitMonth,
    constructionStart,
    constructionEnd,
    deliveryMonth,
    totalMonths,
    defaultAcquisition,
    maxSaleOffset,
    threeSaleWindowFeasible,
    /** Absolute months valid for land acquisition / sale calendar months. */
    minMonth: 1,
    maxMonth: totalMonths,
  };
}

/** Human reason when the three-sale model cannot fit without colliding months. */
export const THREE_SALE_WINDOW_INCOMPLETE_REASON =
  'Programme is too short after permit for three distinct sale months (need at least 2 months between permit and delivery). Increase construction duration, start delay, or delivery buffer.';

export function isThreeSaleWindowFeasible(programmeOrBounds = {}) {
  if (typeof programmeOrBounds.threeSaleWindowFeasible === 'boolean') {
    return programmeOrBounds.threeSaleWindowFeasible;
  }
  const permit = Math.max(1, Number(programmeOrBounds.permitMonth) || 1);
  const delivery = Math.max(
    permit,
    Number(programmeOrBounds.deliveryMonth) || Number(programmeOrBounds.totalMonths) || permit,
  );
  const maxOff = programmeOrBounds.maxSaleOffset != null
    ? Number(programmeOrBounds.maxSaleOffset)
    : delivery - permit;
  return maxOff >= 2;
}

function timingKeys() {
  return {
    pre: 'preBankEndOffsetFromAuthorization',
    post: 'postBankEndOffsetFromAuthorization',
    late: 'lateEndOffsetFromAuthorization',
  };
}

/**
 * Enforce strictly increasing sale offsets: pre < post < late ≤ maxSaleOffset.
 * Cascades later dates forward (and pulls back from delivery when needed)
 * so the stored values are exactly what the engine uses — never equal months.
 */
export function normalizeStrictSaleOffsets(rawTiming = {}, bounds) {
  const keys = timingKeys();
  const maxOff = Math.max(0, bounds?.maxSaleOffset ?? 0);
  const t = rawTiming && typeof rawTiming === 'object' ? rawTiming : {};

  // Need at least offsets 0,1,2 when maxOff >= 2; otherwise compress as far as possible.
  const room = maxOff;
  let pre = clampInt(t[keys.pre], 0, Math.max(0, room), 0);
  let post = clampInt(t[keys.post], 0, Math.max(0, room), 1);
  let late = clampInt(t[keys.late], 0, Math.max(0, room), 2);

  if (room >= 2) {
    pre = Math.min(pre, room - 2);
    if (post <= pre) post = pre + 1;
    post = Math.min(post, room - 1);
    if (late <= post) late = post + 1;
    late = Math.min(late, room);
    // Pull back if cascading overflowed.
    if (late > room) late = room;
    if (post >= late) post = late - 1;
    if (pre >= post) pre = post - 1;
    pre = Math.max(0, pre);
    post = Math.max(pre + 1, post);
    late = Math.max(post + 1, late);
    late = Math.min(room, late);
    post = Math.min(late - 1, post);
    pre = Math.min(post - 1, pre);
  } else {
    // Infeasible three-sale window — keep raw clamped values for display only.
    // Never invent a colliding schedule (e.g. post === late).
    pre = Math.min(pre, room);
    post = Math.min(post, room);
    late = Math.min(late, room);
  }

  const valid = room >= 2 && pre < post && post < late && late <= room;
  return {
    [keys.pre]: pre,
    [keys.post]: post,
    [keys.late]: late,
    valid,
    maxSaleOffset: room,
  };
}

/**
 * Link one sale-month control; cascade later months forward when needed.
 * Returns { salesPhaseTiming, adjustedLater, message }.
 */
export function linkSaleMonthOffsets(current = {}, key, nextOffset, bounds) {
  const keys = timingKeys();
  const keyMap = {
    preBankEndOffsetFromAuthorization: 'pre',
    postBankEndOffsetFromAuthorization: 'post',
    lateEndOffsetFromAuthorization: 'late',
    pre: 'pre',
    post: 'post',
    late: 'late',
  };
  const which = keyMap[key] || key;
  const maxOff = Math.max(0, bounds?.maxSaleOffset ?? 0);
  const base = {
    [keys.pre]: current[keys.pre],
    [keys.post]: current[keys.post],
    [keys.late]: current[keys.late],
  };

  let requested = Math.round(Number(nextOffset));
  if (!Number.isFinite(requested)) requested = base[keys[which === 'pre' ? 'pre' : which === 'post' ? 'post' : 'late']] ?? 0;

  if (which === 'pre') base[keys.pre] = requested;
  else if (which === 'post') base[keys.post] = requested;
  else base[keys.late] = requested;

  const before = { ...base };
  const normalized = normalizeStrictSaleOffsets(base, bounds);
  const adjustedLater =
    (which === 'pre' && (normalized[keys.post] !== before[keys.post] || normalized[keys.late] !== before[keys.late]))
    || (which === 'post' && normalized[keys.late] !== before[keys.late]);

  let message = null;
  if (!normalized.valid) {
    message = `Programme only has ${maxOff} month(s) after permit — need at least 2 to place three distinct sale months.`;
  } else if (adjustedLater) {
    message = 'Later sale months moved forward to keep pre-bank < post-bank < late.';
  } else if (which === 'late' && requested <= (before[keys.post] ?? 0)) {
    message = 'Late sale must be after post-bank.';
  } else if (which === 'post' && requested <= (before[keys.pre] ?? 0)) {
    message = 'Post-bank sale must be after pre-bank.';
  }

  return {
    salesPhaseTiming: {
      [keys.pre]: normalized[keys.pre],
      [keys.post]: normalized[keys.post],
      [keys.late]: normalized[keys.late],
    },
    valid: normalized.valid,
    adjustedLater,
    message,
    maxSaleOffset: maxOff,
  };
}

/** Absolute land month the engine will use (null → prep end). */
export function effectiveLandAcquisitionMonth(rawMonth, bounds) {
  if (rawMonth == null || rawMonth === '') return bounds.defaultAcquisition;
  const m = Math.round(Number(rawMonth));
  if (!Number.isFinite(m)) return bounds.defaultAcquisition;
  return Math.min(bounds.maxMonth, Math.max(1, m));
}

/** Clamp a user land-acquisition input to the visible/engine range. */
export function linkLandAcquisitionMonth(rawMonth, bounds) {
  if (rawMonth == null || rawMonth === '') {
    return { landAcquisitionMonth: null, effective: bounds.defaultAcquisition, message: null };
  }
  const requested = Math.round(Number(rawMonth));
  if (!Number.isFinite(requested)) {
    return { landAcquisitionMonth: null, effective: bounds.defaultAcquisition, message: 'Enter a valid month.' };
  }
  const effective = effectiveLandAcquisitionMonth(requested, bounds);
  const message = effective !== requested
    ? `Land acquisition must be between M1 and M${bounds.maxMonth} (delivery). Using M${effective}.`
    : null;
  return { landAcquisitionMonth: effective, effective, message };
}

export function saleCalendarMonth(offset, bounds) {
  return (bounds.permitMonth || 1) + Math.max(0, Math.round(n(offset)));
}
